/**
 * Collection Tabs Carousel
 *
 * Custom element that manages:
 * - Tab switching with ARIA state
 * - AJAX loading via alternate collection template (/collections/{handle}?view=ctc-ajax)
 * - In-memory cache per collection handle (no duplicate requests)
 * - AbortController for race-condition protection
 * - Swiper initialization / teardown per active panel
 * - Shopify Theme Editor compatibility (shopify:section:load re-init)
 *
 * Multiple instances on the same page work independently — all state is
 * scoped to `this` (the custom element node).
 */

class CollectionTabsCarousel extends HTMLElement {
  connectedCallback() {
    /** @type {Swiper|null} */
    this._swiper = null;

    /** @type {Map<string, string>} collectionHandle -> inner HTML string */
    this._cache = new Map();

    /** @type {AbortController|null} In-flight AJAX request controller */
    this._abortController = null;

    /** @type {string|null} data-block-id of the currently active panel */
    this._activeBlockId = null;

    /** @type {Object|null} Cached Swiper config (built once per instance) */
    this._swiperConfig = null;

    this._sectionId = this.dataset.sectionId;

    this._bindTabClicks();
    this._initFirstTab();

    // Shopify Theme Editor: section settings changed → full re-render
    this._onSectionLoad = (e) => {
      if (e.detail.sectionId !== this._sectionId) return;
      this._destroySwiper();
      this._swiperConfig = null; // rebuild from new data-attributes
      this._cache.clear();       // product count / product order may have changed
      this._activeBlockId = null;
      this._bindTabClicks();
      this._initFirstTab();
    };
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  disconnectedCallback() {
    this._destroySwiper();
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
  }

  // ─── TAB WIRING ───────────────────────────────────────────────────────────

  _bindTabClicks() {
    this.querySelectorAll('[data-tab-btn]').forEach((btn) => {
      // Remove any previous listener before adding a fresh one
      if (btn._ctcClickHandler) {
        btn.removeEventListener('click', btn._ctcClickHandler);
      }
      btn._ctcClickHandler = () => this._onTabClick(btn);
      btn.addEventListener('click', btn._ctcClickHandler);
    });
  }

  _initFirstTab() {
    const firstPanel = this.querySelector('[data-tab-panel].is-active');
    if (!firstPanel) return;
    this._activeBlockId = firstPanel.dataset.blockId;
    this._initSwiperInPanel(firstPanel);
  }

  _onTabClick(btn) {
    if (btn.dataset.blockId === this._activeBlockId) return;
    this._activateTab(btn);
  }

  _activateTab(btn) {
    const blockId      = btn.dataset.blockId;
    const handle       = btn.dataset.collection;
    const shopAllUrl   = btn.dataset.shopAllUrl;

    // ── Update tab button ARIA state ──
    this.querySelectorAll('[data-tab-btn]').forEach((b) => {
      const active = b === btn;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // ── Update Shop All link href ──
    const shopAllLink = this.querySelector('[data-shop-all-link]');
    if (shopAllLink) {
      if (shopAllUrl) {
        shopAllLink.setAttribute('href', shopAllUrl);
        shopAllLink.removeAttribute('hidden');
      } else {
        shopAllLink.setAttribute('hidden', '');
      }
    }

    // ── Switch panels ──
    const panels     = this.querySelectorAll('[data-tab-panel]');
    let targetPanel  = null;

    panels.forEach((panel) => {
      const active = panel.dataset.blockId === blockId;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) targetPanel = panel;
    });

    if (!targetPanel) return;

    this._destroySwiper();
    this._activeBlockId = blockId;

    if (targetPanel.hasAttribute('data-needs-load')) {
      this._loadTab(targetPanel, handle);
    } else {
      // Content already present (server-side or previously cached)
      this._initSwiperInPanel(targetPanel);
    }
  }

  // ─── AJAX LOADING ─────────────────────────────────────────────────────────

  async _loadTab(panel, handle) {
    // Serve from cache when available
    if (this._cache.has(handle)) {
      this._renderContent(panel, this._cache.get(handle));
      return;
    }

    panel.classList.add('is-loading');

    // Cancel any in-flight request (user switched tabs quickly)
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    try {
      /**
       * Uses an alternate collection template (?view=ctc-ajax) instead of the
       * Section Rendering API. The Section Rendering API only renders sections
       * that are already part of the current page's template — a standalone
       * helper section would always return null.
       *
       * templates/collection.ctc-ajax.liquid uses `{% layout none %}` so the
       * response is raw slider HTML with no page chrome around it.
       */
      const url      = `/collections/${encodeURIComponent(handle)}?view=ctc-ajax`;
      const response = await fetch(url, {
        signal:  this._abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();

      this._cache.set(handle, html);
      this._renderContent(panel, html);

    } catch (err) {
      if (err.name === 'AbortError') return; // Expected — another tab was clicked

      console.error('[collection-tabs-carousel] Failed to load tab:', err);
      panel.innerHTML = this._errorHTML();
      panel.removeAttribute('data-needs-load');
    } finally {
      panel.classList.remove('is-loading');
    }
  }

  _renderContent(panel, html) {
    panel.innerHTML = html;
    this._applyProductsLimit(panel);
    panel.removeAttribute('data-needs-load');
    this._initSwiperInPanel(panel);
  }

  _applyProductsLimit(panel) {
    const limit = parseInt(this.dataset.productsLimit || '0', 10);
    if (!limit) return;

    panel.querySelectorAll('.ctc__slide').forEach((slide, index) => {
      if (index >= limit) {
        slide.remove();
      }
    });
  }

  // ─── SWIPER ───────────────────────────────────────────────────────────────

  _initSwiperInPanel(panel) {
    const swiperEl = panel.querySelector('.swiper');
    if (!swiperEl) return;

    if (typeof Swiper === 'undefined') {
      console.warn('[collection-tabs-carousel] Swiper is not loaded.');
      return;
    }

    this._swiper = new Swiper(swiperEl, this._getSwiperConfig());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
  }

  /**
   * Build Swiper config from data-attributes on this element.
   * Result is cached so queries only happen once per instance lifecycle.
   * Passing DOM elements (not CSS selectors) keeps multiple instances isolated.
   */
  _getSwiperConfig() {
    if (this._swiperConfig) return this._swiperConfig;

    const gap            = parseInt(this.dataset.spaceBetween || '16', 10);
    const slidesDesktop  = parseFloat(this.dataset.slidesDesktop  || '4');
    const slidesTablet   = parseFloat(this.dataset.slidesTablet   || '3');
    const slidesMobile   = parseFloat(this.dataset.slidesMobile   || '2');
    const showArrows     = this.dataset.showArrows    === 'true';
    const showDots       = this.dataset.showDots      === 'true';
    const showScrollbar  = this.dataset.showScrollbar === 'true';

    const config = {
      slidesPerView: slidesMobile,
      spaceBetween:  Math.round(gap * 0.6),
      grabCursor:    true,
      watchSlidesProgress: true,
      breakpoints: {
        768: {
          slidesPerView: slidesTablet,
          spaceBetween:  Math.round(gap * 0.8),
        },
        992: {
          slidesPerView: slidesDesktop,
          spaceBetween:  gap,
        },
      },
    };

    if (showArrows) {
      config.navigation = {
        prevEl: this.querySelector('[data-swiper-prev]'),
        nextEl: this.querySelector('[data-swiper-next]'),
      };
    }

    if (showDots) {
      config.pagination = {
        el:        this.querySelector('[data-swiper-pagination]'),
        clickable: true,
      };
    }

    if (showScrollbar) {
      config.scrollbar = {
        el:       this.querySelector('[data-swiper-scrollbar]'),
        draggable: true,
      };
    }

    this._swiperConfig = config;
    return config;
  }

  // ─── HELPERS ──────────────────────────────────────────────────────────────

  _errorHTML() {
    return `<div class="ctc__error">
      <p>Unable to load products. Please try refreshing the page.</p>
    </div>`;
  }
}

// Guard against duplicate registration (e.g. if script is loaded twice)
if (!customElements.get('collection-tabs-carousel')) {
  customElements.define('collection-tabs-carousel', CollectionTabsCarousel);
}
